/**
 * CMS-managed code injection.
 *
 * Lets the client drop analytics, tag managers, chat widgets or verification
 * meta tags into the site without a deploy. Three slots are supported, matching
 * what those snippets normally ask for:
 *
 *   head       -> end of <head>
 *   bodyStart  -> immediately inside <body>
 *   bodyEnd    -> end of <body>
 *
 * Only an authenticated admin can write these values (see firestore.rules) —
 * this is the site owner injecting their own scripts into their own site, which
 * is the entire point of the feature.
 *
 * Snippets are skipped inside /admin so a tag manager cannot interfere with the
 * CMS, and everything injected is removed on unmount.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { siteSettingsService } from "@/services/contentServices";

const MARKER = "data-athena-injected";

/**
 * Scripts added via innerHTML never execute — the HTML spec disables them. So
 * every node is re-created: script tags are rebuilt attribute by attribute,
 * everything else can be adopted as-is.
 */
function materialise(node, doc) {
  if (node.nodeName === "SCRIPT") {
    const script = doc.createElement("script");
    Array.from(node.attributes).forEach((attr) => {
      script.setAttribute(attr.name, attr.value);
    });
    script.text = node.textContent || "";
    return script;
  }
  return node.cloneNode(true);
}

function inject(html, target, position = "beforeend") {
  if (!html || !html.trim() || !target) return [];

  const template = document.createElement("template");
  template.innerHTML = html;

  const inserted = [];
  Array.from(template.content.childNodes).forEach((node) => {
    const element = materialise(node, document);
    if (element.nodeType === Node.ELEMENT_NODE) {
      element.setAttribute(MARKER, "true");
    }
    if (position === "afterbegin" && target.firstChild) {
      target.insertBefore(element, target.firstChild);
    } else {
      target.appendChild(element);
    }
    inserted.push(element);
  });

  return inserted;
}

export default function CodeInjection() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return undefined;

    let cancelled = false;
    let injected = [];

    siteSettingsService
      .getCodeInjection()
      .then((config) => {
        if (cancelled || !config || config.enabled === false) return;
        injected = [
          ...inject(config.head, document.head),
          ...inject(config.bodyStart, document.body, "afterbegin"),
          ...inject(config.bodyEnd, document.body),
        ];
      })
      .catch((error) => {
        // A broken snippet must never take the site down with it.
        // eslint-disable-next-line no-console
        console.error("[CodeInjection] failed to apply snippets", error);
      });

    return () => {
      cancelled = true;
      injected.forEach((element) => element.parentNode?.removeChild(element));
    };
  }, [isAdmin]);

  return null;
}
